import { Paper, CircularProgress, Typography, Box, keyframes } from '@mui/material'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { IStores } from '../Stores'
import ErrorIcon from '@mui/icons-material/Error'
import queryString from 'query-string'

const scaleAnimation = keyframes`
  0%, 100% { 
    transform: scale(1); 
  }
  50% { 
    transform: scale(1.1); 
  }
`

interface IMagicLinkVerifyProps {
  verifyMagicLink?: (
    email: string,
    token: string,
    sessionId?: string
  ) => Promise<{ success: boolean; error?: string; isCrossDevice?: boolean }>
  routing?: any
}

@inject((stores: IStores) => ({
  verifyMagicLink: stores.login.verifyMagicLink,
  routing: stores.routing,
}))
@observer
class MagicLinkVerify extends React.Component<IMagicLinkVerifyProps> {
  public state = {
    status: 'verifying' as 'verifying' | 'success' | 'error',
    errorMessage: '',
    isCrossDevice: false,
  }

  private hasVerified = false

  public componentDidMount() {
    if (this.hasVerified) {
      return
    }
    this.hasVerified = true

    this.verifyToken()
  }

  private verifyToken = async () => {
    const { verifyMagicLink } = this.props

    try {
      const queryParams = queryString.parse(window.location.search)

      const email = Array.isArray(queryParams.email)
        ? queryParams.email[0]
        : queryParams.email

      const magicLinkToken =
        (Array.isArray(queryParams.magicLinkToken)
          ? queryParams.magicLinkToken[0]
          : queryParams.magicLinkToken) ||
        (Array.isArray(queryParams.token) ? queryParams.token[0] : queryParams.token)

      const sessionId = Array.isArray(queryParams.sessionId)
        ? queryParams.sessionId[0]
        : queryParams.sessionId

      if (!email || !magicLinkToken) {
        this.setState({
          status: 'error',
          errorMessage: 'Invalid magic link - missing email or token',
        })
        return
      }

      const result = await verifyMagicLink!(email, magicLinkToken, sessionId || undefined)

      if (result.success) {
        window.history.replaceState({}, document.title, '/magic-link')
        this.setState({
          status: 'success',
          isCrossDevice: result.isCrossDevice || false,
        })
      } else {
        this.setState({
          status: 'error',
          errorMessage: result.error || 'Invalid or expired token',
        })
      }
    } catch (error) {
      this.setState({ status: 'error', errorMessage: 'Error verifying magic link' })
    }
  }

  public render() {
    const { status, errorMessage, isCrossDevice } = this.state

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: 0,
          padding: 0,
          background:
            status === 'error'
              ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
              : 'linear-gradient(135deg, #29348F 0%, #8bb84b 50%, #29348F 100%)',
        }}
      >
        <Paper
          sx={(theme) => ({
            maxWidth: '500px',
            width: '90%',
            p: { xs: 4, sm: 6 },
            borderRadius: 3,
            textAlign: 'center',
            background:
              status === 'error'
                ? theme.palette.mode === 'dark'
                  ? 'rgba(30, 30, 30, 0.6)'
                  : 'rgba(255, 255, 255, 0.5)'
                : theme.palette.mode === 'dark'
                  ? 'rgba(50, 50, 50, 0.3)'
                  : 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border:
              theme.palette.mode === 'dark'
                ? '1px solid rgba(80, 80, 80, 0.4)'
                : '1px solid rgba(255, 255, 255, 0.4)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 8px 32px 0 rgba(0, 0, 0, 0.5)'
                : '0 8px 32px 0 rgba(0, 0, 0, 0.15)',
          })}
        >
          {status === 'verifying' && (
            <Box>
              <CircularProgress
                size={80}
                thickness={3}
                sx={(theme) => ({
                  mb: 3,
                  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                })}
              />
              <Typography
                variant="h4"
                sx={(theme) => ({
                  mb: 2,
                  fontWeight: 500,
                  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                })}
              >
                Verifying...
              </Typography>
              <Typography
                variant="body1"
                sx={(theme) => ({
                  color:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.9)'
                      : 'rgba(0, 0, 0, 0.6)',
                })}
              >
                Please wait while we confirm your authentication.
              </Typography>
            </Box>
          )}

          {status === 'success' && (
            <Box>
              {/* Animated Checkmark */}
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '2rem auto',
                  backgroundColor: 'success.main',
                  boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)',
                  animation: `${scaleAnimation} 0.3s ease-in-out 0.4s both`,
                  position: 'relative',
                  '&::before': {
                    content: '"✓"',
                    fontSize: '60px',
                    color: 'white',
                    fontWeight: 'bold',
                    animation: `${scaleAnimation} 0.3s ease-in-out 0.6s both`,
                  },
                }}
              />

              <Typography
                variant="h4"
                sx={(theme) => ({
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                })}
              >
                All Set!
              </Typography>

              <Typography
                variant="h6"
                sx={(theme) => ({
                  mb: 2,
                  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                })}
              >
                Your login has been confirmed.
              </Typography>

              <Typography
                variant="body1"
                sx={(theme) => ({
                  mb: 1,
                  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                })}
              >
                {isCrossDevice
                  ? 'Return to your browser and continue working.'
                  : 'Redirecting to your dashboard...'}
              </Typography>

              {isCrossDevice && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    mt: 3,
                    fontSize: '0.875rem',
                    color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.7)',
                  })}
                >
                  You can safely close this page.
                </Typography>
              )}
            </Box>
          )}

          {status === 'error' && (
            <Box>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '2rem auto',
                  backgroundColor: 'error.main',
                  boxShadow: '0 4px 20px rgba(245, 87, 108, 0.4)',
                }}
              >
                <ErrorIcon sx={{ fontSize: 60, color: 'white' }} />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'error.main',
                }}
              >
                ✗ Authentication Failed
              </Typography>

              <Typography
                variant="body1"
                sx={(theme) => ({
                  mb: 1,
                  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                })}
              >
                {errorMessage || 'This magic link is invalid or has expired.'}
              </Typography>

              <Typography
                variant="body2"
                sx={(theme) => ({
                  mb: 3,
                  color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.7)',
                })}
              >
                Please try requesting a new magic link.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    )
  }
}

export default MagicLinkVerify
